import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../App.css';
import logo from '../../assets/logo.svg';
import { useNavigate, Link } from 'react-router-dom';
import Loader from '../loader/Loader';
import { TextField, Alert, Button, Typography } from '@mui/material';
import AuthBackground from './AuthBackground';
import AuthContainer from './AuthContainer';
import { logout } from '../../store/slices/authSlice';
import { forgotPassword } from '../../apis/forgotPassword';

// Validation schema
const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email address').required('Email is required').matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Enter a valid email with domain (e.g. user@mail.com)"
      ),
});

const ForgetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [emailSent, setEmailSent] = useState(false);
    const [sentEmail, setSentEmail] = useState('');

    // Auto-logout if user is already logged in (prevents back button access to dashboard)
    useEffect(() => {
        // Check if user exists in localStorage (indicates they navigated here while logged in)
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser || storedToken) {
            // User is logged in, logout them to ensure fresh access
            dispatch(logout());
            // Clear localStorage
            localStorage.removeItem('authUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
        }
    }, [dispatch]); // Only run once on mount

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                await forgotPassword({ email: values.email });
                setEmailSent(true);
                setSentEmail(values.email);
                setTimeout(() => {
                    navigate('/login');
                }, 10000);
            } catch (error) {
                console.error('Forgot password error:', error);
                const errorMessage = typeof error === 'string' ? error : error?.message || 'Failed to send password reset email. Please try again.';
                toast.error(errorMessage);
            }
        },
    });

    return (
        <div className="login_container min-vh-100">
            <AuthBackground />
            <div className="content_container">
                <Loader isLoading={formik.isSubmitting} />
                <div className="content_box">
                    <img src={logo} alt="Logo" className="logo" />
                    <Typography variant="body1" textAlign={'center'}>
                        Forgot your password?
                    </Typography>
                    <AuthContainer>
                        {!emailSent ? (
                            <>
                                <Alert severity="info" sx={{ marginBlock: 2 }}>
                                    We&apos;ll send a password reset link to your email if you have an account with us.
                                </Alert>

                                <form onSubmit={formik.handleSubmit}>
                                    <div className="input_group mb-4">
                                        <label className="input_label">Email Address</label>
                                        <TextField
                                            type="email"
                                            name="email"
                                            placeholder="Enter your email address"
                                            value={formik.values.email}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.email && Boolean(formik.errors.email)}
                                            helperText={formik.touched.email && formik.errors.email}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>

                                    <Button type="submit" variant="contained" disabled={formik.isSubmitting} fullWidth size="small">
                                        {formik.isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            <Alert severity="success" sx={{ 
                                marginBlock: 2, 
                                fontWeight: 'bold',
                                backgroundColor: '#4caf50',
                                color: '#ffffff',
                                '& .MuiAlert-icon': {
                                    color: '#ffffff',
                                },
                            }}>
                                <Typography variant="body1" fontWeight={700} sx={{ marginBottom: 1 }}>
                                    If the email is registered, a password reset link has been sent.
                                </Typography>
                            </Alert>
                        )}

                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                Remember your password?{' '}
                                <Link
                                    to="/login"
                                    style={{
                                        color: '#153d7c',
                                        textDecoration: 'none',
                                        fontWeight: '500',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.textDecoration = 'none';
                                    }}
                                >
                                    Back to Login
                                </Link>
                            </p>
                        </div>
                    </AuthContainer>
                    <p className="copyright">© Pratham Software. All rights reserved.</p>
                </div>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 9999 }}
            />
        </div>
    );
};

export default ForgetPassword;
